// Character select (docs/plans/2026-10-19-character-select.md, section 6): after the sides are drawn, your side's roster
// over the platform (the metro) or the lobby (direct rooms). A tap picks a face, a second tap or Lock in locks it; the
// arrows move the cursor (and pick), Enter locks. The server keeps the picks and the clock (`sel`), and closes it
// (`sel-done`); the grid's rules are in src/shared/select-ui.ts.
import { AVATARS, avatarName, avatarSrc } from '../shared/avatars';
import { Sel, FaceState, faceStates, myPick, moveCursor, selCols, tapMessage, selTitle } from '../shared/select-ui';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

export class SelectView {
  open = false;
  private sel: Sel | null = null;
  /** when `left` runs out (performance.now ms) */
  private ends = 0;
  private cursor = 0;
  private faces: HTMLButtonElement[] = [];
  private shown = '';
  private big!: HTMLImageElement; private nameEl!: HTMLElement; private timer!: HTMLElement; private lock!: HTMLButtonElement; private grid!: HTMLElement;
  private secs = -1;

  /** `me`: your rider number, to tell your pick from the others' */
  constructor(private root: HTMLElement, private send: (m: Record<string, unknown>) => void, private me: () => number, private sfx: (id: 'click' | 'confirm') => void = () => {}) {}

  /** The server's `sel`: open (or refresh) the grid. */
  show(m: Sel) {
    const fresh = !this.open || this.sel?.side !== m.side || this.sel.roster.join() !== m.roster.join();
    this.sel = m; this.ends = performance.now() + Math.max(0, m.left) * 1000; this.open = true;
    if (fresh) { this.build(); const mine = myPick(m.picks, this.me()).av; this.cursor = Math.max(0, m.roster.indexOf(mine)); }
    this.root.hidden = false;
    this.paint();
  }

  /** `sel-done`, or leaving the room: the grid goes. */
  hide() { this.open = false; this.sel = null; this.root.hidden = true; this.root.textContent = ''; this.shown = ''; this.secs = -1; }

  /** Every frame: the timer counts down between the server's updates. */
  tick() {
    if (!this.open) return;
    const s = Math.max(0, Math.ceil((this.ends - performance.now()) / 1000));
    if (s !== this.secs) { this.secs = s; this.timer.textContent = `${s} s`; this.timer.classList.toggle('low', s <= 5); }
  }

  /** Keys while open: arrows, Enter (and Space) here; true when the key was used. */
  key(code: string): boolean {
    if (!this.open || !this.sel) return false;
    const n = this.sel.roster.length;
    if (code.startsWith('Arrow')) {
      const j = moveCursor(this.cursor, code, n, selCols(innerWidth));
      if (j !== this.cursor) { this.cursor = j; const st = this.states()[j]; if (!st.blocked && !myPick(this.sel.picks, this.me()).locked) { this.send({ t: 'pick', av: st.id }); this.sfx('click'); } this.paint(); }
      return true;
    }
    if (code === 'Enter' || code === 'NumpadEnter' || code === 'Space') { this.lockIn(this.sel.roster[this.cursor]); return true; }
    return false;
  }

  private states(): FaceState[] { return this.sel ? faceStates(this.sel.roster, this.sel.picks, this.me()) : []; }

  /** Lock face av in (the cursor's on Enter, your pick on the button). */
  private lockIn(av: number) {
    const st = this.states().find((x) => x.id === av), mine = myPick(this.sel!.picks, this.me());
    if (!st || st.blocked || mine.locked) return;
    this.send({ t: 'pick', av, lock: 1 }); this.sfx('confirm');
  }

  private tap(i: number) {
    if (!this.sel) return;
    const m = tapMessage(this.states(), this.sel.roster[i], myPick(this.sel.picks, this.me()));
    this.cursor = i;
    if (m) { this.send(m); this.sfx(m.lock ? 'confirm' : 'click'); }
    this.paint();
  }

  private build() {
    const m = this.sel!, r = this.root; r.textContent = ''; r.dataset.side = m.side;
    const card = el('div', 'sl');
    const head = el('div', 'sl-head');
    head.append(el('span', 'sl-kick', m.side === 'C' ? 'Your side · Police' : 'Your side · Protesters'), el('span', 'sl-hint', 'Pick a face'));
    const h = el('h2', '', selTitle(m.side)); h.id = 'sel-h';
    this.grid = el('div', 'sl-grid'); this.grid.setAttribute('role', 'radiogroup'); this.grid.setAttribute('aria-labelledby', 'sel-h');
    this.faces = m.roster.map((id, i) => {
      const b = el('button', 'sl-f'); b.type = 'button'; b.dataset.id = String(id); b.setAttribute('role', 'radio'); b.title = AVATARS[id].name;
      const img = el('img'); img.src = avatarSrc(id); img.alt = ''; img.width = img.height = 50; img.decoding = 'async';
      b.append(img, el('span', 'sl-n'));
      b.addEventListener('click', () => this.tap(i));
      this.grid.append(b);
      return b;
    });
    const me = el('div', 'sl-me');
    this.big = el('img', 'sl-big'); this.big.alt = ''; this.big.width = this.big.height = 88;
    const who = el('div', 'sl-who');
    this.nameEl = el('b', 'sl-name'); this.timer = el('span', 'sl-time');
    const t = el('p', 'sl-tl'); t.append(this.timer, el('span', '', ' to pick · tap it again or Enter to lock in'));
    who.append(this.nameEl, t);
    this.lock = el('button', 'btn sl-lock', 'Lock in'); this.lock.type = 'button';
    this.lock.addEventListener('click', () => { const p = myPick(this.sel?.picks ?? [], this.me()); this.lockIn(p.av >= 0 ? p.av : this.sel!.roster[this.cursor]); });
    me.append(this.big, who, this.lock);
    card.append(head, h, this.grid, me);
    r.append(card);
    this.secs = -1; this.tick();
  }

  private paint() {
    const m = this.sel; if (!m) return;
    const sts = this.states(), mine = myPick(m.picks, this.me());
    const key = JSON.stringify([m.picks, this.cursor, this.me()]);
    if (key === this.shown) return;
    this.shown = key;
    sts.forEach((s, i) => {
      const b = this.faces[i], n = b.querySelector('.sl-n')!;
      b.classList.toggle('mine', s.mine); b.classList.toggle('taken', s.by !== null && !s.mine && !s.locked); b.classList.toggle('locked', s.locked);
      b.classList.toggle('hot', i === this.cursor);
      b.disabled = s.blocked || mine.locked;
      b.setAttribute('aria-checked', String(s.mine));
      const who = s.mine ? 'You' : s.by !== null ? `Rider ${s.by}` : '';
      b.setAttribute('aria-label', `${AVATARS[s.id].name}${who ? ` · ${who}${s.locked ? ', locked' : ''}` : ''}`);
      n.textContent = s.mine ? (s.locked ? 'You ✓' : 'You') : s.by !== null ? (s.locked ? `${s.by} ✓` : String(s.by)) : '';
    });
    const show = mine.av >= 0 ? mine.av : m.roster[this.cursor];
    this.big.src = avatarSrc(show); this.big.classList.toggle('none', mine.av < 0);
    this.nameEl.textContent = avatarName(show);
    this.lock.disabled = mine.locked || !!sts.find((s) => s.id === show)?.blocked;
    this.lock.textContent = mine.locked ? 'Locked in' : 'Lock in';
  }
}
